import { useState } from "react";
import { ChevronDown, Upload, User as UserIcon } from "lucide-react";
import authStore from "~/stores/auth";
import { observer } from "mobx-react";
import { NavLink, useNavigate } from "react-router-dom";
import paths from "~/paths";
import { Button } from "~/components/ui/button";
import { UserPermission } from "~/user";
import { cn } from "~/lib/utils";

const BANNER_LINKS: { label: string; href: string }[] = [
  { label: "WFPgo", href: "https://newgo.wfp.org" },
  { label: "Communities", href: "http://communities.wfp.org" },
  { label: "Manuals", href: "https://newgo.wfp.org/documents/list-of-wfp-manuals" },
  { label: "WeLearn", href: "https://welearn.wfp.org" },
  { label: "Dashboard", href: "https://dashboard.wfp.org/" },
  { label: "OPweb", href: "http://opweb.wfp.org" },
  { label: "Self-Service", href: "https://selfservice.go.wfp.org/" },
  { label: "UN Booking Hub", href: "https://unbooking.org/en/" },
  { label: "WFP.org", href: "https://www.wfp.org/" },
];

const TopSitesBar = () => (
  <div className="bg-primary-700 text-white">
    <div className="flex items-center justify-end gap-6 overflow-x-auto whitespace-nowrap px-4 py-1.5 text-xs">
      {BANNER_LINKS.map((link) => (
        // text-white on the <a> itself so it beats the legacy unlayered a{color} rule
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-white hover:underline"
        >
          {link.label}
        </a>
      ))}
    </div>
  </div>
);

const Logo = () => {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer select-none items-center gap-2"
      onClick={() => navigate(paths.construct.index())}
    >
      <img src="/logos/regular/wfp-emblem.svg" alt="WFP" className="h-9 w-auto" />
      <span className="text-base font-bold tracking-wide text-wfp-blue">GODOCS</span>
    </div>
  );
};

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded px-3 py-2 text-base font-semibold hover:bg-[var(--ui-02)]",
    isActive ? "text-wfp-blue" : "[color:var(--text-01)]"
  );

const UserSection = observer(() => {
  const [open, setOpen] = useState(false);
  const userInfo = authStore.userInfo;
  const username = userInfo?.username ?? "N/A";
  const email = userInfo?.email ?? "N/A";

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-2 rounded px-2 py-1 [color:var(--text-01)] hover:bg-[var(--ui-02)]"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {userInfo?.avatar ? (
          <img src={userInfo.avatar} alt="" className="size-8 rounded-full object-cover" />
        ) : (
          <span className="flex size-8 items-center justify-center rounded-full bg-[var(--ui-03)]">
            <UserIcon className="size-4 [color:var(--text-02)]" />
          </span>
        )}
        <span className="hidden max-w-[160px] truncate text-sm sm:inline">{username}</span>
        <ChevronDown className="size-4 [color:var(--text-02)]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-[101] mt-2 w-56 rounded-md border border-[var(--ui-03)] bg-white p-3 shadow-[0px_0px_2px_0px_#00000017,0px_0px_8px_0px_#00000012]">
            {username !== email && (
              <div className="mb-1 truncate text-sm font-semibold [color:var(--text-01)]">{username}</div>
            )}
            <div className="mb-3 truncate text-xs [color:var(--text-02)]">{email}</div>
            <Button size="sm" variant="secondary" className="w-full" onClick={authStore.logOut}>
              Logout
            </Button>
          </div>
        </>
      )}
    </div>
  );
});

const Nav = observer(() => {
  return (
    <header className="fixed inset-x-0 top-0 z-[99] flex h-[87px] flex-col">
      <TopSitesBar />
      <nav className="flex-1 border-b border-[var(--ui-03)] bg-white">
        <div className="flex h-full items-center justify-between gap-4 px-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Logo />
            <NavLink className={navLinkClass} to={paths.construct.documents()} end>
              Home
            </NavLink>
            <NavLink className={navLinkClass} to={paths.construct.personalArea()}>
              Personal area
            </NavLink>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {authStore.hasPermission(UserPermission.UploadDoc) && (
              <Button
                asChild
                variant="default"
                className="ml-1 gap-1.5 border-0 bg-[#0087cb] text-sm font-bold text-white hover:bg-primary-700 hover:text-white"
              >
                <NavLink to={paths.construct.uploadDoc()}>
                  <Upload className="size-4" />
                  <span className="hidden sm:inline">Upload Document</span>
                </NavLink>
              </Button>
            )}
            <UserSection />
          </div>
        </div>
      </nav>
    </header>
  );
});

export default Nav;
